import { SdkError, StatusCode } from '@yandex-video-platform/goloom-sdk';
import { call, put } from 'typed-redux-saga';

import { ErrorPageType } from '../../../shared/components/error-page/types';
import { setErrorPageAction } from '../../../shared/features/config';
import {
    GoloomSdkError,
    KICKED_WHILE_RECONNECTING,
    TelemostBackendErrorCode,
} from '../../../shared/features/goloom-sdk';
import { getCauseObject } from '../../../shared/helpers/get-cause-object';
import { createLogger } from '../../../shared/helpers/loggerFacade';
import { handleCloseRoom, handleKick } from './handlers-sagas';

const logger = createLogger('goloom:error');

/**
 * Обработка ошибок от голума и бекенда
 * @returns Возвращает true, если по ошибке есть редирект на главную/страницу ошибки
 */
export function* handleErrorSaga({ error }: GoloomSdkError) {
    logger.warn(error, {
        method: 'handleErrorSaga',
        additional: {
            code: error.code,
            reason: error.reason,
            cause: getCauseObject(error.cause),
        },
    });

    if (error.code === StatusCode.KICKED_OUT) {
        yield* call(handleKick);

        return true;
    }

    if (error.code === StatusCode.ROOM_HAS_BEEN_CLOSED) {
        logger.event('Room has been closed', {
            additional: {
                method: 'handleErrorSaga',
                feature: 'role_model',
            },
        });
        yield* call(handleCloseRoom);

        return true;
    }

    // код ошибки в SdkError на экране подключения - INIT_CLIENT_ERROR,
    // а первопричина лежит в cause, потому смотрим и туда тоже
    const errorCodes = [error.code, error.cause instanceof SdkError ? error.cause.code : null];
    if (!errorCodes.includes(StatusCode.PERMISSION_DENIED)) {
        return false;
    }

    // на случай, если не прокинули reason в основную ошибку, смотри еще и в первопричину
    const errorReason = error.reason || (error.cause instanceof SdkError && error.cause.reason) || null;

    const telemostBackendCode = errorReason as TelemostBackendErrorCode | typeof KICKED_WHILE_RECONNECTING;

    switch (telemostBackendCode) {
        case TelemostBackendErrorCode.UNKNOWN:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'handleErrorSaga',
                        statusCode: error.code,
                        message: error.message,
                        reason: error.reason,
                    },
                })
            );

            return true;
        case TelemostBackendErrorCode.CONFERENCE_NOT_FOUND:
            yield* put(setErrorPageAction({ type: ErrorPageType.NOT_FOUND }));

            return true;
        case TelemostBackendErrorCode.PEER_NOT_FOUND:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'handleErrorSaga',
                        statusCode: error.code,
                        message: error.message,
                        reason: error.reason,
                    },
                })
            );

            return true;
        case TelemostBackendErrorCode.BAN:
            yield* call(handleKick);

            return true;
        case KICKED_WHILE_RECONNECTING:
        case TelemostBackendErrorCode.KICK:
            yield* call(handleKick);

            return true;
        case TelemostBackendErrorCode.CONFERENCE_OVERFLOW:
            yield* put(setErrorPageAction({ type: ErrorPageType.LIMIT_USERS_COUNT }));

            return true;
        case TelemostBackendErrorCode.CREDENTIALS_EXPIRED:
        case TelemostBackendErrorCode.SESSION_NOT_FOUND:
        case TelemostBackendErrorCode.INCORRECT_CREDENTIALS:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'handleErrorSaga',
                        statusCode: error.code,
                        message: error.message,
                        reason: error.reason,
                    },
                })
            );
            return true;
        default:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'handleErrorSaga',
                        statusCode: error.code,
                        message: error.message,
                        reason: error.reason,
                    },
                })
            );
            return true;
    }
}
